import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsync } from 'react-use';
import { HolderService, TokenService } from 'services';
import { getAddress } from 'viem';
import { DsButton, DsIcon, DsLoader, DsEthAvatar } from '@tokenyico/design-system/dist/reactJS';

import RequestStatus from '../RequestStatus';
import RequestInfoByType from './RequestInfoByType';
import { datetimeFormat } from '@tokenyico/design-system';

const RequestDetail = ({ token, request, onCloseModal, setError }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [requestInfoByType, setRequestInfoByType] = useState();

  useAsync(async () => {
    try {
      setRequestInfoByType(null);

      if (request && request.type === 'CONDITIONAL_TRANSFER' && request.params.recipientWallet) {
        setIsLoading(true);
        const recipient = await HolderService.getHolderByWallet(
          token.id,
          getAddress(request.params.recipientWallet),
        );

        setRequestInfoByType({ recipient: recipient.investor });
        setIsLoading(false);
      }
    } catch (e) {
      setError(e);
      setIsLoading(false);
    }
  }, [request]);

  const handleCancelRequest = async () => {
    setIsLoading(true);
    try {
      await TokenService.postCancelTokenRequests({
        tokenId: token.id,
        requestId: request.id,
      });
    } catch (error) {
      setError(error.message);
    }
    setIsLoading(false);
    onCloseModal(true);
  };
  if (!request) return null;

  return (
    <div style={{ maxWidth: '36rem' }}>
      {isLoading && <DsLoader className="_p(5) _m(5)" />}
      {!isLoading && (
        <>
          <div className="_flex:row _content-space-between:x _m:left(1) _m:top(2) _m:bottom(4)">
            <RequestTitle type={request.type} />
            <RequestStatus status={request.status} size="md" iconSize="14px" />
          </div>

          <div
            className="_grid _text:sm _m:top(1) _m:x(1) _m:bottom(5)"
            style={{ border: '1px solid var(--grey-500)' }}
          >
            <RequestInfoByType
              request={request}
              token={token}
              requestInfoByType={requestInfoByType}
            />
            <div
              className="_grid _col(12) _nogutter _p(2)"
              style={{ borderBottom: '1px solid var(--grey-500)' }}
            >
              <div className="_col(12) _col:md(4) _text:xs _text-grey-900">
                {t('shared.requester')}
              </div>
              <div className="_col(12) _col:md(8)">
                <div className="_flex:row _flex:align(center)">
                  <DsEthAvatar address={request.params.senderWallet} size={14} />
                  <span className="_m:x(2)">
                    {request.requester} ({t('shared.me')})
                  </span>
                </div>
              </div>
            </div>
            <div
              className="_grid _col(12) _nogutter _p(2)"
              style={{ borderBottom: '1px solid var(--grey-500)' }}
            >
              <div className="_col(12) _col:md(4) _text:xs _text-grey-900">
                {t('shared.request')} {t('shared.date').toLocaleLowerCase()}
              </div>
              <div className="_col(12) _col:md(8)">{datetimeFormat(request.createdAt)}</div>
            </div>
            {request.reason && (
              <div
                className="_grid _col(12) _nogutter _p(2)"
                style={{ borderBottom: '1px solid var(--grey-500)' }}
              >
                <div className="_col(12) _col:md(4) _text:xs _text-grey-900">
                  {t('shared.reason')}
                </div>
                <div className="_col(12) _col:md(8)">{request.reason}</div>
              </div>
            )}
          </div>
          <div className="_flex:row  _flex:justify(end) _m:top(5)">
            <DsButton
              version="monochrome"
              label={t('shared.close')}
              type="button"
              onClick={() => onCloseModal()}
            />
            {request.type === 'CONDITIONAL_TRANSFER' &&
              ['INITIATED', 'PENDING_APPROVAL'].includes(request.status) && (
                <>
                  <DsButton
                    label={t('shared.cancelTransfer')}
                    version="danger"
                    icon="close"
                    onClick={handleCancelRequest}
                    class="_m:x(2)"
                    isLoading={isLoading}
                    isDisabled={isLoading}
                    isReverse
                  />
                </>
              )}
          </div>
        </>
      )}
    </div>
  );
};

const RequestTitle = ({ type }) => {
  const { t } = useTranslation();
  if (type === 'WHITELIST') {
    return (
      <div className="_flex:row _text:sm" style={{ alignItems: 'center' }}>
        <DsIcon name="profile-card" size="22px" class="_m:right(2)" />
        <h1 className="h4">
          {t('shared.qualification')} {t('shared.request').toLocaleLowerCase()}
        </h1>
      </div>
    );
  }

  if (type === 'TOKEN_RECOVERY') {
    return (
      <div className="_flex:row _text:sm" style={{ alignItems: 'center' }}>
        <DsIcon name="wallet" size="20px" class="_m:right(2)" />
        <h1 className="h4">
          {t('shared.tokenRecovery')} {t('shared.request').toLocaleLowerCase()}
        </h1>
      </div>
    );
  }

  if (type === 'CONDITIONAL_TRANSFER') {
    return (
      <div className="_flex:row _text:sm" style={{ alignItems: 'center' }}>
        <DsIcon name="transfer" size="22px" class="_m:right(2)" />
        <h1 className="h4">
          {t('shared.transfer')} {t('shared.request').toLocaleLowerCase()}
        </h1>
      </div>
    );
  }

  return (
    <div className="_flex:row _text:sm" style={{ alignItems: 'center' }}>
      <h1 className="h4">{type}</h1>
    </div>
  );
};

export default RequestDetail;
